import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Briefcase, ArrowRight, Loader2 } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import { FIRM } from "../data/siteData";

const ROLES = [
  { title: "Senior Associate — Direct Tax", team: "Taxation", location: "Hyderabad", type: "Full-time" },
  { title: "Company Secretary — ROC Desk", team: "CS & Compliance", location: "Hyderabad / Remote", type: "Full-time" },
  { title: "Valuation Analyst", team: "Fundraising Advisory", location: "Bengaluru", type: "Full-time" },
  { title: "Virtual CFO — Client Partner", team: "CFO Services", location: "Chennai", type: "Full-time" },
  { title: "Corporate Lawyer — Contracts", team: "Legal", location: "Delhi NCR", type: "Full-time" },
  { title: "Article Assistants (CA)", team: "All Practices", location: "Hyderabad", type: "Articleship" },
];

export default function CareersPage() {
  const [form, setForm] = useState({ name: "", email: "", role: "", note: "" });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setForm({ name: "", email: "", role: "", note: "" });
      toast.success(`Application noted. Email your CV to ${FIRM.email} to complete it.`);
    }, 700);
  };

  const inputCls = "w-full rounded-xl border border-brand-blue/15 bg-white/80 px-4 py-3 text-sm text-brand-navy outline-none transition-all placeholder:text-brand-gray/60 focus:ring-2 focus:ring-brand-blue/40";

  return (
    <>
      <SEO
        title="Careers — Join 40+ CA/CS Professionals & 15+ Legal Advisors | Complisure Counsultancy"
        description="Build your career at a fintech-style advisory firm: taxation, secretarial, valuation, Virtual CFO and legal roles across Hyderabad, Bengaluru, Chennai and Delhi NCR."
        canonical="https://compliance-capital-1.preview.emergentagent.com/careers"
        jsonLd={[orgJsonLd, breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }])]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float left-[-8%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">
              Do the best work of your <span className="text-gradient">articleship — or career</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-gray sm:text-lg">
              You'll touch live fundraises, M&A structuring and investor diligence in your first year — work most firms reserve for partners. We hire for hunger, train for craft.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div className="space-y-4">
              {ROLES.map((r, i) => (
                <Reveal key={r.title} delay={i * 0.05}>
                  <div data-testid={`role-card-${i}`} className="glass card-lift flex flex-wrap items-center justify-between gap-4 rounded-2xl p-6">
                    <div>
                      <h2 className="font-display text-lg font-bold text-brand-navy">{r.title}</h2>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-brand-gray">
                        <span className="flex items-center gap-1"><Briefcase className="h-3 w-3 text-brand-blue" /> {r.team}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-brand-blue" /> {r.location}</span>
                        <span className="rounded-full bg-brand-ice px-2.5 py-0.5 font-semibold text-brand-blue">{r.type}</span>
                      </div>
                    </div>
                    <a href={`mailto:${FIRM.email}?subject=Application: ${encodeURIComponent(r.title)}`} data-testid={`role-apply-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue/25 bg-white/70 px-5 py-2.5 text-sm font-semibold text-brand-blue transition-all hover:-translate-y-0.5 hover:bg-brand-ice">
                      Apply <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.12} className="lg:sticky lg:top-28">
              <form data-testid="careers-form" onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8">
                <h2 className="font-display text-xl font-bold text-brand-navy">General application</h2>
                <p className="mt-1.5 text-sm text-brand-gray">Don't see your role? Tell us where you'd fit.</p>
                <div className="mt-5 space-y-4">
                  <input data-testid="careers-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  <input data-testid="careers-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  <input data-testid="careers-role" required placeholder="Role you're interested in" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} />
                  <textarea data-testid="careers-note" rows={3} placeholder="Why Complisure? (2-3 lines)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
                </div>
                <button data-testid="careers-submit" disabled={sending} className="btn-gradient mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Interest <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
